import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

import type { Config } from '../models/config';
import { CONFIG_KEY, LocalStorage } from '../storage';

interface ConfigContextType {
  config: Config | null;
  setConfig: (config: Config) => void;
}

const ConfigContext = createContext<ConfigContextType | null>(null);

interface ConfigProviderProps {
  children: ReactNode;
  initialConfig?: Config;
}

export function ConfigProvider({ children, initialConfig }: ConfigProviderProps) {
  const [config, setConfigState] = useState<Config | null>(initialConfig ?? null);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === CONFIG_KEY && event.newValue) {
        try {
          const updatedConfig = JSON.parse(event.newValue) as Config;
          setConfigState(updatedConfig);
        } catch {
          // Ignore invalid JSON
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const setConfig = (newConfig: Config) => {
    setConfigState(newConfig);
    LocalStorage.setItem(CONFIG_KEY, newConfig);
  };

  return <ConfigContext.Provider value={{ config, setConfig }}>{children}</ConfigContext.Provider>;
}

export function useConfig() {
  const context = useContext(ConfigContext);

  if (context === null) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }

  return context;
}
